// ===== 多设备同步 HTTP 客户端 =====
// 对接自建同步服务（2026-09-17 起正式服务器：www.changfaxianghang.top → 阿里云 8.138.212.242）：
//   GET  /health                     心跳
//   POST /api/pull  { sinceSeq }     增量拉取（含墓碑）→ { records, seq, hasMore }
//   POST /api/push  { deviceId, records } → { results: [{key,status,seq}], seq }
// 鉴权：当前登录会话的 Bearer token。所有请求带超时，绝不裸 fetch（项目铁律）。

import { loadSession } from './authApi';
import type { SyncConfig } from './syncConfig';
import { buildSyncHeaders } from './syncAuth';
import type { SyncPushStatus } from './syncAuth';
export { buildSyncHeaders, isSettledPushStatus } from './syncAuth';

export interface SyncRecord {
  domain: string;
  id: string;
  data: unknown;
  deleted: boolean;
  updatedAt: number;
  deviceId: string;
  /** 服务端分配的单调序号（拉取时存在，推送时无需带） */
  seq?: number;
}

export interface PushResultItem {
  key: string;
  status: SyncPushStatus;
  seq?: number;
  reason?: string;
}

export interface PullResponse {
  records: SyncRecord[];
  seq: number;
  hasMore: boolean;
  serverTime: number;
  /**
   * #A12（2026-09-13）服务端 value-mode 全局接管 seq 映射：
   *  key = domain (e.g. 'products'), value = 服务端最新 __value__ record 的 seq。
   *  客户端拿到后写入 state.valueTakeoverSeq，computeChanges 据此跳过重复整值推送。
   */
  valueTakeoverSeq?: Record<string, number>;
  /**
   * #A15（2026-09-14）服务端可控「强制全量重同步」版本号。
   *  与本地 localStorage 的 changfa_full_resync_v 不一致且 cursor > 0 时，
   *  客户端 cursor 归零重拉一轮（修复 iOS Safari 清了本地存储但残留 cursor 导致数据为空）。
   */
  fullResyncVersion?: number;
}

export interface PushResponse {
  results: PushResultItem[];
  seq: number;
}

export interface HealthResponse {
  ok: boolean;
  service: string;
  records: number;
  seq: number;
  serverTime: number;
}

export class SyncApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** 统一请求：带登录令牌鉴权 + 超时。 */
async function request<T>(
  cfg: SyncConfig,
  path: string,
  body?: unknown,
  timeoutMs = 20000,
): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(cfg.serverUrl.replace(/\/+$/, '') + path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: buildSyncHeaders(loadSession()?.token || ''),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: ctrl.signal,
    });
    if (res.status === 401) throw new SyncApiError('登录已过期，请重新登录（401）', 401);
    if (res.status === 403) throw new SyncApiError('当前账号没有同步此数据的权限（403）', 403);
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json() as { error?: string }).error || ''; } catch { /* ignore */ }
      throw new SyncApiError(detail || `HTTP ${res.status}`, res.status);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

export function syncHealth(cfg: SyncConfig): Promise<HealthResponse> {
  return request<HealthResponse>(cfg, '/health', undefined, 8000);
}

export function syncPull(
  cfg: SyncConfig,
  sinceSeq: number,
  // #A7（2026-09-13）服务端 value 模式：客户端告知哪些域是整值模式，
  // 服务端只对 valueDomains 返回 rid='__value__' 的最新 record，避免下发 4 万条历史 products record。
  valueDomains?: string[],
): Promise<PullResponse> {
  return request<PullResponse>(cfg, '/api/pull', { sinceSeq, valueDomains });
}

export function syncPush(
  cfg: SyncConfig,
  deviceId: string,
  records: SyncRecord[],
): Promise<PushResponse> {
  return request<PushResponse>(cfg, '/api/push', { deviceId, records }, 30000);
}
